import React, { useState, useEffect } from "react";
import { apiCall, useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Radio, Send, MessageCircle, Globe, CheckCircle, Clock, XCircle,
  Download, Upload, Eye, Image as ImageIcon, Plus, RefreshCw, Filter, Calendar, Smartphone,
  Trash2, ShieldCheck, Sparkles
} from "lucide-react";

export default function BroadcastReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "subadmin";
  const [date, setDate] = useState(() => {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return formatter.format(new Date());
  });
  const [selectedChannel, setSelectedChannel] = useState("all");
  const [channels, setChannels] = useState(["Telegram Bot", "Telegram Channels", "WhatsApp", "Brand Name", "SMS"]);
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [retentionDays, setRetentionDays] = useState(7);
  const [cleaning, setCleaning] = useState(false);

  // Upload Modal State
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadChannel, setUploadChannel] = useState("Telegram Bot");
  const [uploadDate, setUploadDate] = useState(date);
  const [uploadRemark, setUploadRemark] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Image Preview Modal
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  // Fetch matrix and summary
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date });
      if (selectedChannel && selectedChannel !== "all") params.set("channel", selectedChannel);

      const [resData, resSummary] = await Promise.all([
        apiCall("get", `/broadcast-reports?${params}`),
        apiCall("get", "/broadcast-reports/dashboard-summary")
      ]);

      setMatrix(resData.data.matrix || []);
      if (resData.data.channels) setChannels(resData.data.channels);
      if (resData.data.retention_days !== undefined) setRetentionDays(resData.data.retention_days);
      setSummary(resSummary.data || null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load broadcast reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date, selectedChannel]);

  // Open Upload Modal
  const handleOpenUpload = (item = null) => {
    if (item) {
      setUploadChannel(item.channel);
      setUploadDate(item.date || date);
      setUploadRemark(item.remark || "");
    } else {
      setUploadChannel(channels[0] || "Telegram Bot");
      setUploadDate(date);
      setUploadRemark("");
    }
    setUploadFile(null);
    setUploadModalOpen(true);
  };

  // Submit Upload
  const handleUploadSubmit = async () => {
    if (!uploadFile) {
      toast.error("Please choose a proof image file");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("channel", uploadChannel);
      formData.append("date", uploadDate || date);
      formData.append("remark", uploadRemark);

      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/broadcast-reports/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
        credentials: "include"
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Upload failed");
      }

      toast.success("Broadcast proof uploaded successfully!");
      setUploadModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || "Failed to upload broadcast proof");
    } finally {
      setUploading(false);
    }
  };

  // Delete single report
  const handleDeleteReport = async (item) => {
    if (!window.confirm(`Are you sure you want to delete the broadcast proof for ${item.channel} on ${item.date || date}?`)) {
      return;
    }
    try {
      if (item.id) {
        await apiCall("delete", `/broadcast-reports/${item.id}`);
      } else {
        await apiCall("post", "/broadcast-reports/delete-by-channel", {
          date: item.date || date,
          channel: item.channel
        });
      }
      toast.success("Broadcast proof deleted successfully");
      setPreviewModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete broadcast report");
    }
  };

  // Trigger manual retention cleanup
  const handleTriggerCleanup = async () => {
    if (!window.confirm(`Run auto-deletion cleanup now? All broadcast reports and images older than ${retentionDays} days will be deleted.`)) {
      return;
    }
    setCleaning(true);
    try {
      const res = await apiCall("post", "/broadcast-reports/cleanup", { days: retentionDays });
      toast.success(res.data?.message || `Cleanup complete! Deleted ${res.data?.deleted_count || 0} expired records.`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to run broadcast cleanup");
    } finally {
      setCleaning(false);
    }
  };

  // Download CSV
  const handleDownloadCSV = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ date });
      if (selectedChannel && selectedChannel !== "all") params.set("channel", selectedChannel);

      const url = `${process.env.REACT_APP_BACKEND_URL}/api/broadcast-reports/download?${params}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      const blob = await response.blob();
      const fileUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = fileUrl;
      a.download = `broadcast_reports_${date}.csv`;
      a.click();
      URL.revokeObjectURL(fileUrl);
      toast.success("Broadcast report CSV downloaded");
    } catch (err) {
      toast.error("Failed to download CSV");
    }
  };

  const getChannelIcon = (ch) => {
    switch (ch) {
      case "Telegram Bot":
        return <Radio className="w-4 h-4 text-blue-500" />;
      case "Telegram Channels":
        return <Send className="w-4 h-4 text-sky-500" />;
      case "WhatsApp":
        return <MessageCircle className="w-4 h-4 text-green-500" />;
      case "SMS":
        return <Smartphone className="w-4 h-4 text-emerald-500" />;
      case "Brand Name":
      default:
        return <Globe className="w-4 h-4 text-purple-500" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="broadcast-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2.5">
              <Radio className="w-7 h-7 text-primary" />
              Broadcast Reports
            </h2>
            <Badge variant="outline" className="text-xs bg-muted/40 font-mono hidden sm:inline-flex items-center gap-1.5 py-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              Auto-purges &gt; {retentionDays > 0 ? `${retentionDays}d` : 'Never'}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            Track daily promotional broadcasts across Telegram Bot, Channels, WhatsApp, and Brand.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleTriggerCleanup}
              disabled={cleaning}
              className="text-xs h-9 border-rose-500/30 text-rose-600 hover:bg-rose-500/10"
              title={`Purge reports and files older than ${retentionDays} days`}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5 text-rose-500" />
              {cleaning ? "Cleaning..." : "Auto-Clean Now"}
            </Button>
          )}
          <Button onClick={() => handleOpenUpload()} className="text-xs h-9">
            <Plus className="w-4 h-4 mr-1.5" /> Upload Broadcast Proof
          </Button>
          <Button variant="outline" onClick={handleDownloadCSV} className="text-xs h-9">
            <Download className="w-4 h-4 mr-1.5" /> Download CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border border-border">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Today's Done</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold text-emerald-600">{summary.today?.done || 0}</span>
                  <span className="text-xs text-muted-foreground">/ {summary.today?.total || 0} required</span>
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Today's Pending</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold text-amber-600">{summary.today?.pending || 0}</span>
                  <span className="text-xs text-muted-foreground">awaiting proof</span>
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Yesterday Done</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold text-emerald-600">{summary.yesterday?.done || 0}</span>
                  <span className="text-xs text-muted-foreground">/ {summary.yesterday?.total || 0}</span>
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Yesterday Missed</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold text-rose-600">{summary.yesterday?.missed || 0}</span>
                  <span className="text-xs text-muted-foreground">not broadcasted</span>
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-rose-500/10 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-rose-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter & Date Selection Bar */}
      <Card className="border border-border">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Picker */}
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <Input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-36 h-8 text-xs font-mono"
              />
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant={date === new Date().toISOString().slice(0, 10) ? "default" : "outline"}
                size="sm" className="text-xs h-8 px-2.5"
                onClick={() => setDate(new Date().toISOString().slice(0, 10))}
              >
                Today
              </Button>
              <Button
                variant="outline" size="sm" className="text-xs h-8 px-2.5"
                onClick={() => {
                  const d = new Date(); d.setDate(d.getDate() - 1);
                  setDate(d.toISOString().slice(0, 10));
                }}
              >
                Yesterday
              </Button>
            </div>

            {/* Channel Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground font-medium">Channel:</span>
              <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                <SelectTrigger className="w-40 h-8 text-xs">
                  <SelectValue placeholder="All Channels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Channels</SelectItem>
                  {channels.map(ch => (
                    <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={fetchData} className="text-xs h-8">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </CardContent>
      </Card>

      {/* Broadcast Matrix Grid */}
      {loading ? (
        <div className="p-12 text-center text-sm text-muted-foreground">Loading broadcast reports...</div>
      ) : matrix.length === 0 ? (
        <div className="p-12 text-center text-sm text-muted-foreground">No broadcast channels found.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {matrix.map((item, idx) => (
            <Card key={idx} className={`border ${item.status === 'done' ? 'border-emerald-500/40 bg-emerald-500/5' : item.status === 'pending' ? 'border-amber-500/40 bg-amber-500/5' : 'border-rose-500/40 bg-rose-500/5'}`}>
              <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
                <div className="flex items-center gap-2">
                  {getChannelIcon(item.channel)}
                  <span className="text-sm font-bold">{item.channel}</span>
                </div>
                <Badge variant={item.status === 'done' ? 'default' : item.status === 'pending' ? 'secondary' : 'destructive'} className="text-[10px] px-1.5 py-0.5">
                  {item.status === 'done' ? '✓ Done' : item.status === 'pending' ? '⏳ Pending' : '✗ Not Broadcasted'}
                </Badge>
              </CardHeader>
              <CardContent className="p-4 pt-2 space-y-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground border-b border-border/40 pb-2">
                  <span className="font-mono text-[11px]">{item.date}</span>
                </div>

                {/* Proof Image / Preview Area */}
                {item.image_url ? (
                  <div
                    className="relative group rounded-md overflow-hidden border border-border bg-card cursor-pointer aspect-video flex items-center justify-center"
                    onClick={() => { setPreviewItem(item); setPreviewModalOpen(true); }}
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
                      alt="Broadcast proof"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 text-white">
                      <Eye className="w-5 h-5" />
                      <span className="text-xs font-semibold">View Proof</span>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-border/80 bg-muted/20 aspect-video flex flex-col items-center justify-center text-muted-foreground p-3 text-center">
                    <ImageIcon className="w-8 h-8 opacity-40 mb-1" />
                    <span className="text-xs">{item.status === 'pending' ? 'No proof uploaded yet' : 'Broadcast missed on this date'}</span>
                  </div>
                )}

                {/* Remark & Upload Info */}
                {item.status === 'done' && (
                  <div className="space-y-1 text-xs">
                    {item.remark && (
                      <p className="text-xs bg-card/80 p-2 rounded border border-border/40 line-clamp-2">
                        "{item.remark}"
                      </p>
                    )}
                    <div className="flex justify-between text-[11px] text-muted-foreground pt-1">
                      <span>By: {item.uploaded_by_name || 'Agent'}</span>
                      <span>{item.uploaded_at ? new Date(item.uploaded_at).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="pt-1 flex items-center gap-2">
                  <Button
                    variant={item.status === 'done' ? 'outline' : 'default'}
                    size="sm"
                    className="flex-1 text-xs h-8"
                    onClick={() => handleOpenUpload(item)}
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                    {item.status === 'done' ? 'Update Proof' : '+ Upload Proof'}
                  </Button>
                  {isAdmin && item.status === 'done' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-rose-500 hover:text-rose-600 hover:bg-rose-500/10"
                      title="Delete proof"
                      onClick={() => handleDeleteReport(item)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* POPUP: UPLOAD PROOF MODAL */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <Upload className="w-4 h-4 text-primary" />
              Upload Broadcast Proof
            </DialogTitle>
          </DialogHeader>
            <div className="space-y-3 pt-2">
            <div className="space-y-1">
                <label className="text-xs font-semibold">Channel:</label>
                <Select value={uploadChannel} onValueChange={setUploadChannel}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Select channel" />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.map(ch => (
                      <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold">Broadcast Date:</label>
              <Input
                type="date"
                value={uploadDate}
                onChange={e => setUploadDate(e.target.value)}
                className="h-9 text-xs font-mono"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold">Proof Screenshot (Image):</label>
              <Input
                type="file"
                accept="image/*"
                onChange={e => setUploadFile(e.target.files[0])}
                className="h-9 text-xs cursor-pointer"
              />
              {uploadFile && (
                <span className="text-[11px] text-emerald-600 block">Selected: {uploadFile.name}</span>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold">Remark / Notes:</label>
              <Textarea
                placeholder="e.g., Broadcast sent to 15k active users with promo code..."
                value={uploadRemark}
                onChange={e => setUploadRemark(e.target.value)}
                rows={3}
                className="text-xs"
              />
            </div>
          </div>
          <DialogFooter className="pt-3">
            <Button variant="outline" size="sm" onClick={() => setUploadModalOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleUploadSubmit} disabled={uploading}>
              {uploading ? "Uploading..." : "Submit Proof"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* POPUP: IMAGE LIGHTBOX PREVIEW */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center justify-between">
              <span>{previewItem?.channel}</span>
              <span className="text-xs font-normal text-muted-foreground font-mono">{previewItem?.date}</span>
            </DialogTitle>
          </DialogHeader>
          {previewItem && (
            <div className="space-y-3 pt-2">
              <div className="max-h-[65vh] overflow-auto rounded-lg border border-border flex items-center justify-center bg-black/5">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${previewItem.image_url}`}
                  alt="Broadcast proof preview"
                  className="max-h-[60vh] max-w-full object-contain"
                />
              </div>

              {previewItem.remark && (
                <div className="p-3 bg-muted/40 rounded-lg text-xs">
                  <span className="font-semibold text-muted-foreground block mb-1">Remark:</span>
                  <p className="text-foreground">{previewItem.remark}</p>
                </div>
              )}

              <div className="flex justify-between items-center text-xs text-muted-foreground pt-1 flex-wrap gap-2">
                <span>Uploaded by: <strong className="text-foreground">{previewItem.uploaded_by_name || 'Agent'}</strong></span>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="destructive"
                      className="text-xs h-8"
                      onClick={() => handleDeleteReport(previewItem)}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Proof
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-8"
                    onClick={() => {
                      const a = document.createElement("a");
                      a.href = `${process.env.REACT_APP_BACKEND_URL}${previewItem.image_url}`;
                      a.download = `broadcast_${previewItem.channel}_${previewItem.date}.png`;
                      a.target = "_blank";
                      a.click();
                    }}
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Download Image
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
